<script lang="ts">
  import { db } from '../../lib/firebase';
  import { authState } from '../../lib/auth.svelte';
  import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
  import Button from '../ui/Button.svelte';
  import LoadingSpinner from '../ui/LoadingSpinner.svelte';
  import WebhookModal from './WebhookModal.svelte';
  import { WEBHOOK_EVENTS, type Webhook, type WebhookEventId } from '../../lib/types';

  type Props = {
    businessId: string | null;
  };

  let { businessId }: Props = $props();

  let webhooks = $state<Webhook[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let showModal = $state(false);
  let editingWebhook = $state<Webhook | null>(null);
  let testingWebhook = $state<string | null>(null);

  // Listen to webhooks collection
  $effect(() => {
    if (!businessId) {
      loading = false;
      return;
    }

    loading = true;
    const q = query(
      collection(db, 'businessdata', businessId, 'webhooks'),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      webhooks = snap.docs.map(d => ({ id: d.id, ...d.data() } as Webhook));
      loading = false;
    }, (err) => {
      console.error('Error loading webhooks:', err);
      error = 'Failed to load webhooks';
      loading = false;
    });

    return () => unsub();
  });

  async function handleCreate(data: { url: string; events: WebhookEventId[]; signingSecret: string }) {
    if (!businessId || !authState.user) return;

    try {
      await addDoc(collection(db, 'businessdata', businessId, 'webhooks'), {
        url: data.url,
        events: data.events,
        signingSecret: data.signingSecret || null,
        status: 'active',
        createdAt: new Date().toISOString(),
        createdBy: authState.user.uid,
        updatedAt: new Date().toISOString(),
        lastTriggeredAt: null,
        lastTriggeredStatus: null,
      });
    } catch (err: any) {
      console.error('Error creating webhook:', err);
      error = 'Failed to create webhook';
    }
  }

  async function handleSave(data: { url: string; events: WebhookEventId[]; signingSecret: string }) {
    if (editingWebhook && businessId) {
      try {
        await updateDoc(doc(db, 'businessdata', businessId, 'webhooks', editingWebhook.id), {
          url: data.url,
          events: data.events,
          signingSecret: data.signingSecret || null,
          updatedAt: new Date().toISOString(),
        });
      } catch (err: any) {
        console.error('Error updating webhook:', err);
        error = 'Failed to update webhook';
      }
    } else {
      await handleCreate(data);
    }

    showModal = false;
    editingWebhook = null;
  }

  async function handleDelete(webhook: Webhook) {
    if (!businessId) return;

    const confirmed = window.confirm('Delete this webhook? This cannot be undone.');
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'businessdata', businessId, 'webhooks', webhook.id));
    } catch (err: any) {
      console.error('Error deleting webhook:', err);
      error = 'Failed to delete webhook';
    }
  }

  async function handleToggleStatus(webhook: Webhook) {
    if (!businessId) return;

    const newStatus = webhook.status === 'active' ? 'inactive' : 'active';

    try {
      await updateDoc(doc(db, 'businessdata', businessId, 'webhooks', webhook.id), {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      });
    } catch (err: any) {
      console.error('Error toggling webhook status:', err);
      error = 'Failed to update webhook status';
    }
  }

  function handleTest(webhook: Webhook) {
    testingWebhook = webhook.id;
    setTimeout(() => {
      testingWebhook = null;
    }, 2000);
  }

  function truncateUrl(url: string): string {
    try {
      const parsed = new URL(url);
      const path = parsed.pathname.length > 20 ? parsed.pathname.substring(0, 20) + '...' : parsed.pathname;
      return parsed.host + path;
    } catch {
      return url.length > 40 ? url.substring(0, 40) + '...' : url;
    }
  }

  function formatRelative(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  }
</script>

<section class="section">
  <div class="section-header">
    <div>
      <h2>Webhooks</h2>
      <p class="section-description">Receive real-time notifications when events occur in EQUOS.</p>
    </div>
    <Button variant="primary" onclick={() => { editingWebhook = null; showModal = true; }} disabled={!businessId}>
      Add Webhook
    </Button>
  </div>

  {#if error}
    <div class="error-banner">
      <span>{error}</span>
      <button class="error-dismiss" onclick={() => error = null}>&times;</button>
    </div>
  {/if}

  {#if loading}
    <div class="loading-state">
      <LoadingSpinner />
      <p>Loading webhooks...</p>
    </div>
  {:else if webhooks.length === 0}
    <div class="webhook-preview">
      <h4>Available Events</h4>
      <div class="events-grid">
        {#each WEBHOOK_EVENTS as event}
          <div class="event-item">
            <span class="event-dot"></span>
            <span>{event.label}</span>
          </div>
        {/each}
      </div>
      <p class="empty-note">Add a webhook endpoint to start receiving event notifications.</p>
    </div>
  {:else}
    <div class="webhooks-list">
      {#each webhooks as webhook (webhook.id)}
        <div class="webhook-row">
          <div class="webhook-info">
            <div class="webhook-url-row">
              <code class="webhook-url" title={webhook.url}>{truncateUrl(webhook.url)}</code>
              <span class="status-badge" class:status-active={webhook.status === 'active'} class:status-inactive={webhook.status === 'inactive'}>
                {webhook.status}
              </span>
            </div>
            <div class="webhook-meta">
              <span>{webhook.events.length} event{webhook.events.length !== 1 ? 's' : ''}</span>
              {#if webhook.lastTriggeredAt}
                <span class="meta-separator">|</span>
                <span>Last triggered: {formatRelative(webhook.lastTriggeredAt)}</span>
              {/if}
            </div>
          </div>
          <div class="webhook-actions">
            <Button variant="icon" title={webhook.status === 'active' ? 'Pause' : 'Resume'} onclick={() => handleToggleStatus(webhook)}>
              {#if webhook.status === 'active'}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="4" y="3" width="2.5" height="10" rx="0.5" />
                  <rect x="9.5" y="3" width="2.5" height="10" rx="0.5" />
                </svg>
              {:else}
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="5,3 13,8 5,13" />
                </svg>
              {/if}
            </Button>
            <Button variant="icon" title="Test" onclick={() => handleTest(webhook)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M13.5 8A5.5 5.5 0 1 1 8 2.5" />
                <polyline points="8 5 8 8 10.5 9.5" />
              </svg>
            </Button>
            <Button variant="icon" title="Edit" onclick={() => { editingWebhook = webhook; showModal = true; }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M11.5 2.5a1.77 1.77 0 0 1 2.5 2.5L5.5 13.5 2 14l.5-3.5z" />
              </svg>
            </Button>
            <Button variant="icon" title="Delete" onclick={() => handleDelete(webhook)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2.5 4.5h11" />
                <path d="M5.5 4.5V3a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1.5" />
                <path d="M12 4.5l-.5 8.5a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1L4 4.5" />
                <line x1="6.5" y1="7" x2="6.5" y2="11" />
                <line x1="9.5" y1="7" x2="9.5" y2="11" />
              </svg>
            </Button>
          </div>
        </div>
      {/each}
    </div>
  {/if}

  {#if testingWebhook}
    <div class="info-banner" style="margin-top: 1rem;">
      Webhook testing requires backend setup. This feature will be available soon.
    </div>
  {/if}
</section>

{#if showModal}
  <WebhookModal
    webhook={editingWebhook}
    onClose={() => { showModal = false; editingWebhook = null; }}
    onSave={handleSave}
  />
{/if}

<style>
  .section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
  }

  .section-header h2 {
    margin: 0 0 0.25rem;
    font-size: 1.25rem;
    color: #333;
  }

  .section-description {
    margin: 0;
    color: #666;
    font-size: 0.9rem;
  }

  .error-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-bottom: 1rem;
    color: #991b1b;
    font-size: 0.875rem;
  }

  .error-dismiss {
    background: none;
    border: none;
    cursor: pointer;
    color: #991b1b;
    font-size: 1.25rem;
    padding: 0;
    line-height: 1;
  }

  .error-dismiss:hover {
    opacity: 0.7;
  }

  /* Webhook Preview (empty state) */
  .webhook-preview {
    background: #f9fafb;
    border-radius: 8px;
    padding: 1.5rem;
  }

  .webhook-preview h4 {
    margin: 0 0 1rem;
    font-size: 0.95rem;
    color: #333;
  }

  .events-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .event-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    color: #555;
  }

  .event-dot {
    width: 8px;
    height: 8px;
    background: #667eea;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .empty-note {
    margin: 0;
    font-size: 0.85rem;
    color: #999;
    font-style: italic;
  }

  /* Webhooks List */
  .webhooks-list {
    display: flex;
    flex-direction: column;
  }

  .webhook-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 0;
    border-bottom: 1px solid #f3f4f6;
    gap: 1rem;
  }

  .webhook-row:first-child {
    padding-top: 0;
  }

  .webhook-row:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .webhook-info {
    flex: 1;
    min-width: 0;
  }

  .webhook-url-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.375rem;
    flex-wrap: wrap;
  }

  .webhook-url {
    font-family: 'SF Mono', 'Fira Code', 'Fira Mono', Menlo, Consolas, monospace;
    font-size: 0.875rem;
    background: #f3f4f6;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    color: #374151;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 300px;
  }

  .webhook-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    color: #9ca3af;
  }

  .meta-separator {
    color: #d1d5db;
  }

  .webhook-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    flex-shrink: 0;
  }

  .webhook-actions :global(.btn-icon) {
    color: #6b7280;
  }

  .webhook-actions :global(.btn-icon:hover) {
    color: #374151;
  }

  @media (max-width: 640px) {
    .webhook-row {
      flex-direction: column;
      align-items: flex-start;
    }

    .webhook-actions {
      align-self: flex-end;
    }

    .webhook-url {
      max-width: 220px;
    }
  }
</style>
